import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { PRIVACY_SETTINGS_EVENT } from "lib/privacy-settings";
import styles from "./privacy-consent.module.css";

export interface PrivacyChoices {
  analytics: boolean;
  marketing: boolean;
}

interface PrivacyConsentProps {
  locale: string;
  ready: boolean;
  consent: PrivacyChoices | null;
  onSave: (choices: PrivacyChoices) => void;
}

const COPY = {
  en: {
    title: "Your privacy choices",
    introduction:
      "We use essential storage to remember your choices. With your permission, we also use analytics to understand visits and marketing tools to measure advertising.",
    accept: "Accept all",
    reject: "Reject optional",
    customize: "Choose settings",
    description:
      "Choose which optional tools may run. Change your choices or withdraw permission at any time via Cookie settings in the footer.",
    essential: "Essential",
    essentialDescription: "Remembers your choices and uses BugSnag for limited technical error reports to keep the website working.",
    alwaysOn: "Always active",
    analytics: "Analytics",
    analyticsDescription: "Allows Google Analytics to measure visits and successful forms, and Vercel Speed Insights to measure website performance.",
    marketing: "Marketing",
    marketingDescription: "Allows Meta tools to measure advertising and form conversions.",
    save: "Save choices",
    close: "Close settings",
  },
  "nl-be": {
    title: "Jouw privacykeuzes",
    introduction:
      "We gebruiken noodzakelijke opslag om je keuzes te onthouden. Met jouw toestemming gebruiken we ook analytics om bezoeken te begrijpen en marketingtools om advertenties te meten.",
    accept: "Alles toestaan",
    reject: "Weigeren",
    customize: "Instellingen kiezen",
    description:
      "Kies welke optionele tools mogen werken. Je kunt je keuzes altijd aanpassen of toestemming intrekken via Cookie-instellingen onderaan de pagina.",
    essential: "Noodzakelijk",
    essentialDescription: "Onthoudt je keuzes en gebruikt BugSnag voor beperkte technische foutmeldingen om de website goed te laten werken.",
    alwaysOn: "Altijd actief",
    analytics: "Analytics",
    analyticsDescription: "Laat Google Analytics bezoeken en geslaagde formulieren meten, en Vercel Speed Insights de websiteprestaties.",
    marketing: "Marketing",
    marketingDescription: "Laat Meta-tools advertenties en formulierconversies meten.",
    save: "Keuzes opslaan",
    close: "Instellingen sluiten",
  },
  "tr-tr": {
    title: "Gizlilik tercihleriniz",
    introduction:
      "Tercihlerinizi hatırlamak için gerekli depolamayı kullanıyoruz. İzninizle ziyaretleri anlamak için analiz, reklamları ölçmek için pazarlama araçları da kullanıyoruz.",
    accept: "Tümünü kabul et",
    reject: "İsteğe bağlıları reddet",
    customize: "Tercihleri seç",
    description:
      "Hangi isteğe bağlı araçların çalışabileceğini seçin. Sayfa altındaki Çerez ayarlarından tercihlerinizi değiştirebilir veya izninizi geri çekebilirsiniz.",
    essential: "Gerekli",
    essentialDescription: "Tercihlerinizi hatırlar ve sitenin çalışması için BugSnag ile sınırlı teknik hata raporları gönderir.",
    alwaysOn: "Her zaman etkin",
    analytics: "Analiz",
    analyticsDescription: "Google Analytics'in ziyaretleri ve başarılı formları, Vercel Speed Insights'ın site performansını ölçmesine izin verir.",
    marketing: "Pazarlama",
    marketingDescription: "Meta araçlarının reklamları ve form dönüşümlerini ölçmesine izin verir.",
    save: "Tercihleri kaydet",
    close: "Ayarları kapat",
  },
};

const DENIED: PrivacyChoices = { analytics: false, marketing: false };
const GRANTED: PrivacyChoices = { analytics: true, marketing: true };

export default function PrivacyConsent({ locale, ready, consent, onSave }: PrivacyConsentProps) {
  const copy = COPY[locale === "nl-be" || locale === "tr-tr" ? locale : "en"];
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState<PrivacyChoices>(DENIED);
  const id = useId();

  const openPreferences = useCallback(() => {
    setDraft(consent ? { ...consent } : { ...DENIED });
    if (!dialogRef.current?.open) dialogRef.current?.showModal();
  }, [consent]);

  useEffect(() => {
    if (!ready) return;
    window.addEventListener(PRIVACY_SETTINGS_EVENT, openPreferences);
    return () => window.removeEventListener(PRIVACY_SETTINGS_EVENT, openPreferences);
  }, [ready, openPreferences]);

  function save(choices: PrivacyChoices) {
    dialogRef.current?.close();
    onSave({ analytics: choices.analytics, marketing: choices.marketing });
  }

  function keepFocusInDialog(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== "Tab") return;
    const controls = event.currentTarget.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled])");
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  if (!ready) return null;

  return (
    <>
      {consent === null ? (
        <section className={styles.banner} aria-labelledby={`${id}-banner-title`}>
          <div className={styles.bannerCopy}>
            <h2 id={`${id}-banner-title`} className={styles.heading}>{copy.title}</h2>
            <p className={styles.description}>{copy.introduction}</p>
          </div>
          <div className={styles.bannerActions}>
            <div className={styles.equalActions}>
              <button type="button" className={styles.choiceButton} onClick={() => save(GRANTED)}>{copy.accept}</button>
              <button type="button" className={styles.choiceButton} onClick={() => save(DENIED)}>{copy.reject}</button>
            </div>
            <button type="button" className={styles.customizeButton} onClick={openPreferences} aria-haspopup="dialog">{copy.customize}</button>
          </div>
        </section>
      ) : null}

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={`${id}-dialog-title`}
        aria-describedby={`${id}-dialog-description`}
        onKeyDown={keepFocusInDialog}
      >
        <div className={styles.dialogHeader}>
          <h2 id={`${id}-dialog-title`} className={styles.heading}>{copy.title}</h2>
          <button type="button" className={styles.closeButton} onClick={() => dialogRef.current?.close()} aria-label={copy.close}>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <p id={`${id}-dialog-description`} className={styles.description}>{copy.description}</p>

        <div className={styles.options}>
          <div className={styles.essentialOption}>
            <div>
              <h3 className={styles.optionTitle}>{copy.essential}</h3>
              <p className={styles.optionDescription}>{copy.essentialDescription}</p>
            </div>
            <span className={styles.alwaysOn}>{copy.alwaysOn}</span>
          </div>
          <label className={styles.option} htmlFor={`${id}-analytics`}>
            <span>
              <span id={`${id}-analytics-title`} className={styles.optionTitle}>{copy.analytics}</span>
              <span id={`${id}-analytics-description`} className={styles.optionDescription}>{copy.analyticsDescription}</span>
            </span>
            <span className={styles.switchControl}>
              <input
                id={`${id}-analytics`}
                type="checkbox"
                role="switch"
                checked={draft.analytics}
                aria-labelledby={`${id}-analytics-title`}
                aria-describedby={`${id}-analytics-description`}
                onChange={(event) => setDraft((current) => ({ ...current, analytics: event.target.checked }))}
              />
              <span className={styles.switchTrack} aria-hidden="true" />
            </span>
          </label>
          <label className={styles.option} htmlFor={`${id}-marketing`}>
            <span>
              <span id={`${id}-marketing-title`} className={styles.optionTitle}>{copy.marketing}</span>
              <span id={`${id}-marketing-description`} className={styles.optionDescription}>{copy.marketingDescription}</span>
            </span>
            <span className={styles.switchControl}>
              <input
                id={`${id}-marketing`}
                type="checkbox"
                role="switch"
                checked={draft.marketing}
                aria-labelledby={`${id}-marketing-title`}
                aria-describedby={`${id}-marketing-description`}
                onChange={(event) => setDraft((current) => ({ ...current, marketing: event.target.checked }))}
              />
              <span className={styles.switchTrack} aria-hidden="true" />
            </span>
          </label>
        </div>

        <div className={styles.equalActions}>
          <button type="button" className={styles.choiceButton} onClick={() => save(draft)}>{copy.save}</button>
          <button type="button" className={styles.choiceButton} onClick={() => save(DENIED)}>{copy.reject}</button>
        </div>
      </dialog>
    </>
  );
}
